import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DeleteIcon from "@mui/icons-material/Delete";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useGetList, useGetManyReference } from "react-admin";
import { AddAppointmentDialog } from "../../dialogs/AddAppointmentDialog/AddAppointmentDialog";
import { useDialog } from "../../hooks/useDialog";
import { useCurrentDrivingSchoolId } from "../../hooks/useCurrentDrivingSchoolId";
import { useEventDialog } from "../../hooks/useEventDialog";
import { DateRange } from "../../model/DateRange";
import { DateWithoutTime } from "../../model/DateWithoutTime";
import { AutovioCalendarEvent, isDrivingLesson } from "../../model/autovioCalendarEvents";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import { CalendarEventCardContent } from "../../misc/calendar/CalendarEventCardContent";
import { overviewCardStyle } from "../../misc/backofficeTheme";
import { PageTitle } from "../../misc/PageTitle";
import { VehicleIcon } from "../../icons/VehicleIcon";
import { Instructor } from "../../providers/instructorsProvider";
import { Vehicle } from "../../providers/vehiclesProvider";
import { calendarEventBackgroundColor, calendarEventTextColor, calendarEventTitle } from "../../utils/calendar";
import { safeLocalStorage } from "../../utils/safeLocalStorage";
import { grants } from "../../backoffice.access_control";
import { AccessDenied } from "../../misc/AccessDenied";

type SchedulerViewMode = "day" | "threeDays" | "week" | "month";
type SchedulerResourceType = "instructor" | "vehicle" | "simulator";

interface SchedulerResourceSelection {
  type: SchedulerResourceType;
  id: string;
}

interface SchedulerResource extends SchedulerResourceSelection {
  name: string;
  avatarUrl?: string;
  instructor?: Instructor;
  vehicle?: Vehicle;
}

const SCHEDULER_START_HOUR = 6;
const SCHEDULER_END_HOUR = 22;
const HOUR_COLUMN_WIDTH_PX = 64;
const RESOURCE_COLUMN_WIDTH_PX = 220;
const RESOURCE_ROW_HEIGHT_PX = 58;
const EVENT_LABEL_MIN_VISIBLE_CHARACTERS = 3;
const EVENT_LABEL_AVERAGE_CHARACTER_WIDTH_PX = 7;
const EVENT_LABEL_HORIZONTAL_PADDING_PX = 16;
const MONTH_EVENT_MIN_WIDTH_PX = 4;
const MONTH_SLOT_WIDTH_RATIO_TO_WEEK_SLOT = 2 / 3;
const RIGHT_BROWSER_PADDING_PX = 40;

export function DrivingSchoolScheduler() {
  if (!grants.has("Scheduler:view")) {
    return <AccessDenied />;
  }
  return <_DrivingSchoolScheduler />;
}

function _DrivingSchoolScheduler() {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const [viewMode, setViewMode] = useSchedulerViewMode(drivingSchoolId);
  const [startDate, setStartDate] = useState(() => DateTime.now().setZone("Europe/Berlin").startOf("day"));
  const [addResourceDialogOpen, setAddResourceDialogOpen] = useState(false);
  const [appointmentInstructor, setAppointmentInstructor] = useState<Instructor>();
  const [selectedResources, setSelectedResources] = useSchedulerResourceSelections(drivingSchoolId);
  const { dialogProps: addAppointmentDialogProps, openDialog: openAddAppointmentDialog } = useDialog();
  const { eventDialog, openEventDialog } = useEventDialog();
  const schedulerContainerRef = useRef<HTMLDivElement>(null);
  const [schedulerContainerWidth, setSchedulerContainerWidth] = useState<number>();
  const days = useMemo(() => _buildVisibleDays(startDate, viewMode), [startDate, viewMode]);
  const dateRange = useMemo<DateRange>(() => {
    const firstDay = DateWithoutTime.fromDateTime(days[0]);
    const lastDay = DateWithoutTime.fromDateTime(days[days.length - 1]);
    return [firstDay, lastDay];
  }, [days]);
  const { data: instructors = [] } = useGetList<Instructor>(
    "instructors",
    {
      filter: { drivingSchoolId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !!drivingSchoolId },
  );
  const { data: vehicles = [] } = useGetList<Vehicle>(
    "vehicles",
    {
      filter: { drivingSchoolId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !!drivingSchoolId },
  );
  const { data: events = [] } = useGetManyReference<AutovioCalendarEvent>(
    "calendarEvents",
    {
      target: "drivingSchoolId",
      id: drivingSchoolId ?? "",
      filter: { dateRange },
      pagination: { page: 1, perPage: 9999 },
      sort: { field: "start", order: "ASC" },
    },
    { enabled: !!drivingSchoolId },
  );
  const resources = useMemo(
    () => _resolveSelectedResources(selectedResources, instructors, vehicles),
    [instructors, selectedResources, vehicles],
  );
  const visibleEvents = useMemo(() => events.filter(_isVisibleSchedulerEvent), [events]);
  const goToPreviousRange = () => setStartDate((date) => date.minus(_getRangeStep(viewMode)));
  const goToNextRange = () => setStartDate((date) => date.plus(_getRangeStep(viewMode)));
  const goToToday = () => setStartDate(DateTime.now().setZone("Europe/Berlin").startOf("day"));
  const addResources = (resources: SchedulerResourceSelection[]) => {
    setSelectedResources((current) => [...current, ...resources]);
    setAddResourceDialogOpen(false);
  };
  const removeResource = (resource: SchedulerResourceSelection) => {
    setSelectedResources((current) => current.filter((it) => !_isSameResource(it, resource)));
  };
  const reorderResource = (sourceResourceKey: string, destinationResourceKey: string) => {
    setSelectedResources((current) => {
      const sourceIndex = current.findIndex((it) => _resourceKey(it) === sourceResourceKey);
      const destinationIndex = current.findIndex((it) => _resourceKey(it) === destinationResourceKey);
      if (sourceIndex < 0 || destinationIndex < 0 || sourceIndex === destinationIndex) {
        return current;
      }
      return _moveArrayItem(current, sourceIndex, destinationIndex);
    });
  };
  const removeAllResources = () => {
    setSelectedResources([]);
  };
  const openCreateAppointmentDialog = (instructor: Instructor, initialDateTime: DateTime) => {
    AddAppointmentDialog.initialDateTime = initialDateTime;
    setAppointmentInstructor(instructor);
    openAddAppointmentDialog();
  };
  useEffect(() => {
    const updateSchedulerContainerWidth = () => {
      const left = schedulerContainerRef.current?.getBoundingClientRect().left ?? 0;
      setSchedulerContainerWidth(Math.max(320, window.innerWidth - left - RIGHT_BROWSER_PADDING_PX));
    };
    updateSchedulerContainerWidth();
    window.addEventListener("resize", updateSchedulerContainerWidth);
    const resizeObserver = new ResizeObserver(updateSchedulerContainerWidth);
    if (schedulerContainerRef.current?.parentElement) {
      resizeObserver.observe(schedulerContainerRef.current.parentElement);
    }
    return () => {
      window.removeEventListener("resize", updateSchedulerContainerWidth);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Box ref={schedulerContainerRef} sx={{ width: schedulerContainerWidth ?? "100%", minWidth: 0 }}>
      <PageTitle>Terminplaner</PageTitle>
      <Box
        sx={{
          ...overviewCardStyle,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0, mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, mr: "auto" }}>{_formatDateRange(days)}</Typography>
          <IconButton size="small" onClick={goToPreviousRange} aria-label="Vorheriger Zeitraum">
            <NavigateBeforeIcon />
          </IconButton>
          <Button variant="outlined" size="small" onClick={goToToday}>
            Heute
          </Button>
          <IconButton size="small" onClick={goToNextRange} aria-label="Nächster Zeitraum">
            <NavigateNextIcon />
          </IconButton>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            sx={schedulerViewToggleGroupSx}
            onChange={(_, value: SchedulerViewMode | null) => value && setViewMode(value)}
          >
            <ToggleButton value="day" sx={schedulerViewToggleButtonSx}>
              1 Tag
            </ToggleButton>
            <ToggleButton value="threeDays" sx={schedulerViewToggleButtonSx}>
              3 Tage
            </ToggleButton>
            <ToggleButton value="week" sx={schedulerViewToggleButtonSx}>
              Woche
            </ToggleButton>
            <ToggleButton value="month" sx={schedulerViewToggleButtonSx}>
              Monat
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <SchedulerGrid
          days={days}
          viewMode={viewMode}
          resources={resources}
          events={visibleEvents}
          onRemoveResource={removeResource}
          onReorderResource={reorderResource}
          onRemoveAllResources={removeAllResources}
          onAddResource={() => setAddResourceDialogOpen(true)}
          onCreateAppointment={openCreateAppointmentDialog}
          onOpenEventDialog={openEventDialog}
        />
      </Box>
      {eventDialog}
      <AddAppointmentDialog
        {...addAppointmentDialogProps}
        instructor={appointmentInstructor}
        tabs={["drivingLesson", "exam", "otherEvent"]}
      />
      <AddSchedulerResourceDialog
        open={addResourceDialogOpen}
        selectedResources={selectedResources}
        instructors={instructors}
        vehicles={vehicles}
        onClose={() => setAddResourceDialogOpen(false)}
        onAddResources={addResources}
      />
    </Box>
  );
}

function SchedulerGrid(props: {
  days: DateTime[];
  viewMode: SchedulerViewMode;
  resources: SchedulerResource[];
  events: AutovioCalendarEvent[];
  onRemoveResource: (resource: SchedulerResourceSelection) => void;
  onReorderResource: (sourceResourceKey: string, destinationResourceKey: string) => void;
  onRemoveAllResources: () => void;
  onAddResource: () => void;
  onCreateAppointment: (instructor: Instructor, initialDateTime: DateTime) => void;
  onOpenEventDialog: (event: AutovioCalendarEvent) => void;
}) {
  const {
    days,
    viewMode,
    resources,
    events,
    onAddResource,
    onCreateAppointment,
    onOpenEventDialog,
    onRemoveAllResources,
    onRemoveResource,
    onReorderResource,
  } = props;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollContainerWidth, setScrollContainerWidth] = useState<number>();
  const slotDurationHours = _getSlotDurationHours(viewMode);
  const schedulerHours = _buildSchedulerHours(slotDurationHours);
  const totalSlots = days.length * schedulerHours.length;
  const resourceRowHeight = RESOURCE_ROW_HEIGHT_PX;
  const slotWidth = _getSlotWidth(viewMode, scrollContainerWidth, totalSlots);
  const columnWidth = `${slotWidth}px`;
  const gridTemplateColumns = `${RESOURCE_COLUMN_WIDTH_PX}px repeat(${totalSlots}, ${columnWidth})`;
  const gridMinWidth = RESOURCE_COLUMN_WIDTH_PX + totalSlots * slotWidth;

  useEffect(() => {
    const updateScrollContainerWidth = () => setScrollContainerWidth(scrollContainerRef.current?.clientWidth);
    updateScrollContainerWidth();
    const resizeObserver = new ResizeObserver(updateScrollContainerWidth);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);

  const handleDragEnd = (result: DropResult) => {
    const destination = result.destination;
    if (!destination || destination.index === result.source.index) {
      return;
    }
    const destinationResource = resources[destination.index];
    if (destinationResource) {
      onReorderResource(result.draggableId, _resourceKey(destinationResource));
    }
  };

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        width: "100%",
        maxWidth: "100%",
        maxHeight: "calc(100vh - 260px)",
        overflow: "auto",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Box sx={{ minWidth: gridMinWidth }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns,
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Box sx={{ ...stickyHeaderCellSx, borderBottom: viewMode === "month" ? "1px solid" : 0 }} />
          {days.map((day) => (
            <Box key={day.toISODate()} sx={dayHeaderSx(schedulerHours.length, day, viewMode)}>
              <SchedulerDayHeader day={day} viewMode={viewMode} />
            </Box>
          ))}
          {viewMode !== "month" && (
            <>
              <Box sx={stickyHeaderCellSx} />
              {days.flatMap((day) =>
                schedulerHours.map((hour) => (
                  <Box key={`${day.toISODate()}-${hour}`} sx={hourHeaderSx}>
                    {_formatHour(hour)}
                  </Box>
                )),
              )}
            </>
          )}
        </Box>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="scheduler-resources" direction="vertical">
            {(provided) => (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {resources.map((resource, index) => {
                  const resourceKey = _resourceKey(resource);
                  const resourceEvents = _eventsForResource(events, resource);
                  return (
                    <Draggable key={resourceKey} draggableId={resourceKey} index={index}>
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={provided.draggableProps.style}
                          sx={{
                            position: "relative",
                            display: "grid",
                            gridTemplateColumns,
                            minHeight: resourceRowHeight,
                            boxShadow: snapshot.isDragging ? 3 : undefined,
                          }}
                        >
                          <Box sx={{ ...resourceCellSx, minHeight: resourceRowHeight }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                              <IconButton
                                {...provided.dragHandleProps}
                                className="scheduler-resource-drag-handle"
                                size="small"
                                sx={resourceDragHandleButtonSx}
                                aria-label={`Reihenfolge von ${resource.name} ändern`}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </IconButton>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                <SchedulerResourceIcon resource={resource} />
                                <Box sx={{ minWidth: 0 }}>
                                  <SchedulerResourceName resource={resource} />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                    {_resourceTypeLabel(resource.type)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => onRemoveResource(resource)}
                              aria-label="Ressource entfernen"
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Box
                            sx={{
                              position: "relative",
                              gridColumn: "2 / -1",
                              display: "grid",
                              gridTemplateColumns: `repeat(${totalSlots}, ${columnWidth})`,
                            }}
                          >
                            {days.flatMap((day) =>
                              schedulerHours.map((hour) => {
                                const instructor = resource.instructor;
                                const canCreateAppointment = !!instructor;
                                return (
                                  <Box
                                    key={`${resource.type}-${resource.id}-${day.toISODate()}-${hour}`}
                                    component={canCreateAppointment ? "button" : "div"}
                                    type={canCreateAppointment ? "button" : undefined}
                                    aria-label={
                                      canCreateAppointment
                                        ? `Termin für ${resource.name} am ${day
                                            .setLocale("de")
                                            .toFormat("dd.MM.yyyy")} um ${_formatHour(hour)} erstellen`
                                        : undefined
                                    }
                                    sx={slotCellSx(canCreateAppointment, resourceRowHeight)}
                                    onClick={() => {
                                      if (canCreateAppointment && instructor) {
                                        onCreateAppointment(instructor, _slotDateTime(day, hour));
                                      }
                                    }}
                                  />
                                );
                              }),
                            )}
                            {resourceEvents.map((event) => (
                              <SchedulerEventBlock
                                key={`${resource.type}-${resource.id}-${event.id}`}
                                event={event}
                                days={days}
                                resourceRowHeight={resourceRowHeight}
                                slotDurationHours={slotDurationHours}
                                slotWidth={slotWidth}
                                slotsPerDay={schedulerHours.length}
                                viewMode={viewMode}
                                onOpenEventDialog={onOpenEventDialog}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
        <Box sx={{ display: "grid", gridTemplateColumns, minHeight: resourceRowHeight }}>
          <Box sx={{ ...addResourceCellSx, minHeight: resourceRowHeight }}>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onAddResource}>
              Hinzufügen
            </Button>
            {resources.length > 1 && (
              <Tooltip title="Alle entfernen" arrow>
                <IconButton
                  className="remove-all-resources-button"
                  size="small"
                  onClick={onRemoveAllResources}
                  aria-label="Alle Ressourcen entfernen"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {days.flatMap((day) =>
            schedulerHours.map((hour) => (
              <Box key={`add-resource-${day.toISODate()}-${hour}`} sx={slotCellSx(false, resourceRowHeight)} />
            )),
          )}
        </Box>
      </Box>
    </Box>
  );
}

function SchedulerDayHeader(props: { day: DateTime; viewMode: SchedulerViewMode }) {
  const { day, viewMode } = props;
  if (viewMode === "month") {
    return (
      <Box sx={{ display: "grid", justifyItems: "center", lineHeight: 1.1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{day.toFormat("d")}</Typography>
        <Typography color="text.secondary" sx={{ fontSize: 11, fontWeight: 400 }}>
          {day.setLocale("de").toFormat("ccc")}
        </Typography>
      </Box>
    );
  }
  return <>{_formatDayHeader(day, viewMode)}</>;
}

function SchedulerResourceName(props: { resource: SchedulerResource }) {
  const { resource } = props;
  const nameRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const detailPath = _resourceDetailPath(resource);

  useEffect(() => {
    const updateIsTruncated = () => {
      const element = nameRef.current;
      setIsTruncated(!!element && element.scrollWidth > element.clientWidth);
    };
    updateIsTruncated();
    const resizeObserver = new ResizeObserver(updateIsTruncated);
    if (nameRef.current) {
      resizeObserver.observe(nameRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [resource.name]);

  return (
    <Tooltip title={isTruncated ? resource.name : ""} arrow placement="top">
      <Box component="span" sx={{ minWidth: 0, display: "block" }}>
        <Link
          to={detailPath}
          sx={{
            minWidth: 0,
            display: "block",
            color: "inherit",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <Typography
            component="span"
            ref={nameRef}
            sx={{
              display: "block",
              fontWeight: 700,
              fontSize: 14,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {resource.name}
          </Typography>
        </Link>
      </Box>
    </Tooltip>
  );
}

function SchedulerResourceIcon(props: { resource: SchedulerResource }) {
  const { resource } = props;
  const theme = useTheme();
  if (resource.type === "instructor") {
    return <Avatar src={resource.avatarUrl} sx={resourceAvatarSx} />;
  }
  if (!resource.vehicle) {
    return <Box sx={resourceIconSx} />;
  }
  return (
    <Box sx={resourceIconSx}>
      <VehicleIcon vehicle={resource.vehicle} fontSize="small" htmlColor={theme.palette.text.secondary} />
    </Box>
  );
}

function SchedulerEventBlock(props: {
  event: AutovioCalendarEvent;
  days: DateTime[];
  resourceRowHeight: number;
  slotDurationHours: number;
  slotWidth: number;
  slotsPerDay: number;
  viewMode: SchedulerViewMode;
  onOpenEventDialog: (event: AutovioCalendarEvent) => void;
}) {
  const { event, days, onOpenEventDialog, resourceRowHeight, slotDurationHours, slotWidth, slotsPerDay, viewMode } =
    props;
  const placement = _getEventPlacement(event, days, slotDurationHours, slotWidth, slotsPerDay, viewMode);
  if (!placement) {
    return null;
  }
  const label = _eventLabel(event);
  const showLabel =
    viewMode !== "month" && _getEventLabelCharacterCapacity(placement.width) >= EVENT_LABEL_MIN_VISIBLE_CHARACTERS;
  const backgroundColor = calendarEventBackgroundColor(event);
  const color = calendarEventTextColor(event);
  const isPrivateEvent = _isPrivateSchedulerEvent(event);

  return (
    <Tooltip title={<SchedulerEventTooltip event={event} isPrivateEvent={isPrivateEvent} />} arrow placement="top">
      <Box
        component="button"
        type="button"
        tabIndex={0}
        aria-label={`${_eventLabel(event)} ${_formatTime(event.start)} bis ${_formatTime(event.end)}`}
        onClick={() => onOpenEventDialog(event)}
        sx={{
          position: "absolute",
          left: placement.left,
          top: 10,
          width: placement.width,
          height: resourceRowHeight - 20,
          boxSizing: "border-box",
          px: viewMode === "month" ? 0.5 : showLabel ? 1 : 0.5,
          py: 0,
          display: "flex",
          alignItems: "center",
          border: 0,
          borderRadius: viewMode === "week" ? 0.5 : 1,
          appearance: "none",
          backgroundColor,
          color,
          cursor: "pointer",
          fontSize: viewMode === "month" ? 11 : 12,
          fontWeight: 700,
          overflow: "hidden",
          zIndex: 1,
        }}
      >
        {showLabel && (
          <Box
            component="span"
            sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {label}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

function SchedulerEventTooltip(props: { event: AutovioCalendarEvent; isPrivateEvent: boolean }) {
  const { event, isPrivateEvent } = props;
  if (isPrivateEvent) {
    return <SchedulerPrivateEventTooltip event={event} />;
  }
  return (
    <Box sx={{ minWidth: 180 }}>
      {!isDrivingLesson(event) && <SchedulerEventTimeRange event={event} />}
      <CalendarEventCardContent event={event} mode="Tooltip" />
    </Box>
  );
}

function SchedulerPrivateEventTooltip(props: { event: AutovioCalendarEvent }) {
  const { event } = props;
  return (
    <Box>
      <SchedulerEventTimeRange event={event} />
      <Typography sx={{ color: "inherit", fontWeight: 700, fontSize: 12 }}>{_eventLabel(event)}</Typography>
    </Box>
  );
}

function SchedulerEventTimeRange(props: { event: AutovioCalendarEvent }) {
  const { event } = props;
  return (
    <Typography sx={{ color: "inherit", fontSize: 12 }}>{`${_formatTime(event.start)} - ${_formatTime(
      event.end,
    )}`}</Typography>
  );
}

function AddSchedulerResourceDialog(props: {
  open: boolean;
  selectedResources: SchedulerResourceSelection[];
  instructors: Instructor[];
  vehicles: Vehicle[];
  onClose: () => void;
  onAddResources: (resources: SchedulerResourceSelection[]) => void;
}) {
  const { open, selectedResources, instructors, vehicles, onClose, onAddResources } = props;
  const [selectedInstructors, setSelectedInstructors] = useState<ResourcePickerItem[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<ResourcePickerItem[]>([]);
  const [selectedSimulators, setSelectedSimulators] = useState<ResourcePickerItem[]>([]);
  const availableInstructors = instructors.filter(
    (it) => it.isActive && !_isResourceSelected(selectedResources, "instructor", it.id),
  );
  const availableVehicles = vehicles.filter(
    (it) => !it.deleted && it.type !== "simulator" && !_isResourceSelected(selectedResources, "vehicle", it.id),
  );
  const simulators = vehicles.filter((it) => !it.deleted && it.type === "simulator");
  const availableSimulators = simulators.filter((it) => !_isResourceSelected(selectedResources, "simulator", it.id));
  const selectedCount = selectedInstructors.length + selectedVehicles.length + selectedSimulators.length;
  const addSelectedResources = () => {
    onAddResources([
      ...selectedInstructors.map((it) => ({ type: "instructor" as const, id: it.id })),
      ...selectedVehicles.map((it) => ({ type: "vehicle" as const, id: it.id })),
      ...selectedSimulators.map((it) => ({ type: "simulator" as const, id: it.id })),
    ]);
  };

  useEffect(() => {
    if (!open) {
      setSelectedInstructors([]);
      setSelectedVehicles([]);
      setSelectedSimulators([]);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Ressourcen hinzufügen</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent sx={{ display: "grid", gap: 2, pt: 1, pb: 3 }}>
        <ResourcePickerSection
          title="Fahrlehrer"
          placeholder="Fahrlehrer auswählen"
          emptyText="Alle Fahrlehrer wurden bereits hinzugefügt."
          items={availableInstructors.map((it) => ({ id: it.id, name: it.name }))}
          selectAllLabel="Alle Fahrlehrer auswählen"
          value={selectedInstructors}
          onChange={setSelectedInstructors}
        />
        <ResourcePickerSection
          title="Fahrzeuge"
          placeholder="Fahrzeuge auswählen"
          emptyText="Alle Fahrzeuge wurden bereits hinzugefügt."
          items={availableVehicles.map((it) => ({ id: it.id, name: _vehicleName(it) }))}
          selectAllLabel="Alle Fahrzeuge auswählen"
          value={selectedVehicles}
          onChange={setSelectedVehicles}
        />
        {simulators.length > 0 && (
          <ResourcePickerSection
            title="Simulatoren"
            placeholder="Simulatoren auswählen"
            emptyText="Alle Simulatoren wurden bereits hinzugefügt."
            items={availableSimulators.map((it) => ({ id: it.id, name: _vehicleName(it) }))}
            selectAllLabel="Alle Simulatoren auswählen"
            value={selectedSimulators}
            onChange={setSelectedSimulators}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={addSelectedResources} disabled={selectedCount === 0}>
          Hinzufügen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface ResourcePickerItem {
  id: string;
  name: string;
}

function ResourcePickerSection(props: {
  title: string;
  placeholder: string;
  emptyText: string;
  items: ResourcePickerItem[];
  selectAllLabel: string;
  value: ResourcePickerItem[];
  onChange: (items: ResourcePickerItem[]) => void;
}) {
  const { title, placeholder, emptyText, items, selectAllLabel, value, onChange } = props;
  return (
    <Box>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={items}
        value={value}
        noOptionsText={emptyText}
        sx={{ mt: 0.75 }}
        getOptionLabel={(item) => item.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        onChange={(_, items) => onChange(items)}
        renderInput={(params) => (
          <TextField {...params} inputProps={{ ...params.inputProps, "aria-label": title }} placeholder={placeholder} />
        )}
      />
      {items.length > 0 && (
        <MuiLink component="button" type="button" sx={selectAllResourcesLinkSx} onClick={() => onChange(items)}>
          {selectAllLabel}
        </MuiLink>
      )}
    </Box>
  );
}

function useSchedulerResourceSelections(drivingSchoolId: string | undefined) {
  const storageKey = drivingSchoolId ? `DrivingSchoolScheduler.resources.${drivingSchoolId}` : undefined;
  const [resources, setResources] = useState<SchedulerResourceSelection[]>(() =>
    storageKey ? (safeLocalStorage.getItem(storageKey, JSON.parse) ?? []) : [],
  );
  useEffect(() => {
    if (!storageKey) {
      setResources([]);
      return;
    }
    setResources(safeLocalStorage.getItem(storageKey, JSON.parse) ?? []);
  }, [storageKey]);
  useEffect(() => {
    if (storageKey) {
      safeLocalStorage.setItem(storageKey, resources, JSON.stringify);
    }
  }, [resources, storageKey]);
  return [resources, setResources] as const;
}

function useSchedulerViewMode(drivingSchoolId: string | undefined) {
  const storageKey = drivingSchoolId ? `DrivingSchoolScheduler.viewMode.${drivingSchoolId}` : undefined;
  const [viewMode, setViewMode] = useState<SchedulerViewMode>(() =>
    storageKey ? _parseSchedulerViewMode(safeLocalStorage.getItem(storageKey, JSON.parse)) : "threeDays",
  );
  useEffect(() => {
    if (!storageKey) {
      setViewMode("threeDays");
      return;
    }
    setViewMode(_parseSchedulerViewMode(safeLocalStorage.getItem(storageKey, JSON.parse)));
  }, [storageKey]);
  useEffect(() => {
    if (storageKey) {
      safeLocalStorage.setItem(storageKey, viewMode, JSON.stringify);
    }
  }, [storageKey, viewMode]);
  return [viewMode, setViewMode] as const;
}

function _buildVisibleDays(startDate: DateTime, viewMode: SchedulerViewMode): DateTime[] {
  if (viewMode === "month") {
    const firstDay = startDate.startOf("month");
    const numberOfDays = firstDay.daysInMonth ?? 31;
    return Array.from({ length: numberOfDays }, (_, index) => firstDay.plus({ days: index }));
  }
  const normalizedStartDate = viewMode === "week" ? startDate.startOf("week") : startDate.startOf("day");
  return Array.from({ length: _getNumberOfDays(viewMode) }, (_, index) => normalizedStartDate.plus({ days: index }));
}

function _getRangeStep(viewMode: SchedulerViewMode): { days?: number; months?: number } {
  if (viewMode === "month") return { months: 1 };
  return { days: _getNumberOfDays(viewMode) };
}

function _getNumberOfDays(viewMode: SchedulerViewMode): number {
  if (viewMode === "day") return 1;
  if (viewMode === "threeDays") return 3;
  return 7;
}

function _parseSchedulerViewMode(value: unknown): SchedulerViewMode {
  if (value === "day" || value === "threeDays" || value === "week" || value === "month") {
    return value;
  }
  return "threeDays";
}

function _getSlotDurationHours(viewMode: SchedulerViewMode): number {
  if (viewMode === "month") return 24;
  if (viewMode === "week") return 6;
  return viewMode === "threeDays" ? 3 : 1;
}

function _getSlotWidth(
  viewMode: SchedulerViewMode,
  scrollContainerWidth: number | undefined,
  totalSlots: number,
): number {
  if (!scrollContainerWidth) {
    return HOUR_COLUMN_WIDTH_PX;
  }
  if (viewMode === "month") {
    const weekSlotCount = 7 * _buildSchedulerHours(_getSlotDurationHours("week")).length;
    const weekSlotWidth = (scrollContainerWidth - RESOURCE_COLUMN_WIDTH_PX) / weekSlotCount;
    return Math.max(48, weekSlotWidth * MONTH_SLOT_WIDTH_RATIO_TO_WEEK_SLOT);
  }
  return Math.max(HOUR_COLUMN_WIDTH_PX, (scrollContainerWidth - RESOURCE_COLUMN_WIDTH_PX) / totalSlots);
}

function _buildSchedulerHours(slotDurationHours: number): number[] {
  const numberOfSlots = Math.ceil((SCHEDULER_END_HOUR - SCHEDULER_START_HOUR) / slotDurationHours);
  return Array.from({ length: numberOfSlots }, (_, index) => SCHEDULER_START_HOUR + index * slotDurationHours);
}

function _resolveSelectedResources(
  selectedResources: SchedulerResourceSelection[],
  instructors: Instructor[],
  vehicles: Vehicle[],
): SchedulerResource[] {
  return selectedResources.flatMap((resource): SchedulerResource[] => {
    if (resource.type === "instructor") {
      const instructor = instructors.find((it) => it.id === resource.id);
      return instructor ? [{ ...resource, name: instructor.name, avatarUrl: instructor.avatarUrl, instructor }] : [];
    }
    const vehicle = vehicles.find((it) => it.id === resource.id);
    return vehicle ? [{ ...resource, name: _vehicleName(vehicle), vehicle }] : [];
  });
}

function _eventsForResource(events: AutovioCalendarEvent[], resource: SchedulerResource): AutovioCalendarEvent[] {
  return events.filter((event) => {
    if (resource.type === "instructor") {
      const instructorUids = (event as any).derived?.instructorUids ?? [];
      return (event as any).instructorId === resource.id || instructorUids.includes(resource.instructor?.autovioUserId);
    }
    return (
      event.resources?.includes(resource.id) ||
      (event as any).vehicleId === resource.id ||
      (event as any).vehicle2Id === resource.id
    );
  });
}

function _getEventPlacement(
  event: AutovioCalendarEvent,
  days: DateTime[],
  slotDurationHours: number,
  slotWidth: number,
  slotsPerDay: number,
  viewMode: SchedulerViewMode,
): { left: number; width: number } | null {
  // Known limitation: scheduler events are placed only on their start day. Multi-day or overnight events are not
  // split into separate visible day segments because these appointments are not expected in normal backoffice use.
  const dayIndex = days.findIndex((day) => event.start.hasSame(day, "day"));
  if (dayIndex < 0) {
    return null;
  }
  const visibleStart = days[dayIndex].set({ hour: SCHEDULER_START_HOUR, minute: 0, second: 0, millisecond: 0 });
  const visibleEnd = days[dayIndex].set({ hour: SCHEDULER_END_HOUR, minute: 0, second: 0, millisecond: 0 });
  const eventStart = DateTime.max(event.start, visibleStart);
  const eventEnd = DateTime.min(event.end, visibleEnd);
  if (eventEnd <= visibleStart || eventStart >= visibleEnd) {
    return null;
  }
  if (viewMode === "month") {
    const visibleDurationHours = SCHEDULER_END_HOUR - SCHEDULER_START_HOUR;
    const startOffsetHours = eventStart.diff(visibleStart, "hours").hours;
    const durationHours = eventEnd.diff(eventStart, "hours").hours;
    const width = Math.min(
      slotWidth,
      Math.max(MONTH_EVENT_MIN_WIDTH_PX, (durationHours / visibleDurationHours) * slotWidth),
    );
    return {
      left: dayIndex * slotWidth + Math.min((startOffsetHours / visibleDurationHours) * slotWidth, slotWidth - width),
      width,
    };
  }
  const dayOffset = dayIndex * slotsPerDay * slotWidth;
  const startOffsetHours = eventStart.diff(visibleStart, "hours").hours;
  const durationHours = Math.max(eventEnd.diff(eventStart, "hours").hours, 0.25);
  return {
    left: dayOffset + (startOffsetHours / slotDurationHours) * slotWidth,
    width: (durationHours / slotDurationHours) * slotWidth,
  };
}

function _isVisibleSchedulerEvent(event: AutovioCalendarEvent): boolean {
  const studentRsvp = (event as any).student?.rsvp;
  const status = (event as any).status;
  return (
    !event.deleted &&
    !["rejected", "declined", "canceled"].includes(studentRsvp) &&
    !String(status ?? "").startsWith("canceled")
  );
}

function _isPrivateSchedulerEvent(event: AutovioCalendarEvent): boolean {
  const label = _eventLabel(event).toLowerCase();
  return (
    (event as any).title === "Privater Termin" ||
    (event as any).type === "PrivateAppointment" ||
    label.startsWith("privat")
  );
}

function _isResourceSelected(
  resources: SchedulerResourceSelection[],
  type: SchedulerResourceType,
  id: string,
): boolean {
  return resources.some((resource) => resource.type === type && resource.id === id);
}

function _isSameResource(a: SchedulerResourceSelection, b: SchedulerResourceSelection): boolean {
  return a.type === b.type && a.id === b.id;
}

function _resourceKey(resource: SchedulerResourceSelection): string {
  return `${resource.type}:${resource.id}`;
}

function _moveArrayItem<T>(items: T[], sourceIndex: number, destinationIndex: number): T[] {
  const result = [...items];
  const [item] = result.splice(sourceIndex, 1);
  result.splice(destinationIndex, 0, item);
  return result;
}

function _vehicleName(vehicle: Vehicle): string {
  return vehicle.plateNumber ? `${vehicle.name} (${vehicle.plateNumber})` : vehicle.name;
}

function _resourceTypeLabel(type: SchedulerResourceType): string {
  if (type === "instructor") return "Fahrlehrer";
  if (type === "simulator") return "Simulator";
  return "Fahrzeug";
}

function _resourceDetailPath(resource: SchedulerResource): string {
  if (resource.type === "instructor") {
    return `/instructors/${resource.id}`;
  }
  if (resource.type === "simulator") {
    return `/simulators/${resource.id}`;
  }
  return `/vehicles/${resource.id}`;
}

function _eventLabel(event: AutovioCalendarEvent): string {
  return calendarEventTitle(event);
}

function _getEventLabelCharacterCapacity(width: number): number {
  return Math.floor((width - EVENT_LABEL_HORIZONTAL_PADDING_PX) / EVENT_LABEL_AVERAGE_CHARACTER_WIDTH_PX);
}

function _formatDateRange(days: DateTime[]): string {
  const firstDay = days[0].setLocale("de");
  const lastDay = days[days.length - 1].setLocale("de");
  if (firstDay.hasSame(lastDay, "day")) {
    return firstDay.toFormat("cccc, d. LLLL yyyy");
  }
  return `${firstDay.toFormat("d. LLL")} - ${lastDay.toFormat("d. LLL yyyy")}`;
}

function _formatDayHeader(day: DateTime, viewMode: SchedulerViewMode): string {
  const dayFormat = viewMode === "week" ? "ccc" : "cccc";
  const formattedDate = day.setLocale("de").toFormat(`${dayFormat}, d. LLL yyyy`);
  return day.hasSame(DateTime.now().setZone("Europe/Berlin"), "day") ? `${formattedDate} (Heute)` : formattedDate;
}

function _formatHour(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

function _slotDateTime(day: DateTime, hour: number): DateTime {
  return day.set({ hour, minute: 0, second: 0, millisecond: 0 });
}

function _formatTime(dateTime: DateTime): string {
  return dateTime.setLocale("de").toFormat("HH:mm");
}

const stickyHeaderCellSx = {
  position: "sticky",
  left: 0,
  zIndex: 6,
  backgroundColor: "background.paper",
  borderRight: "1px solid",
  borderBottom: "1px solid",
  borderColor: "divider",
};

const dayHeaderSx = (numberOfHours: number, day: DateTime, viewMode: SchedulerViewMode) => ({
  position: "relative",
  gridColumn: `span ${numberOfHours}`,
  px: 1.5,
  py: 1.25,
  fontWeight: 700,
  fontSize: 14,
  borderBottom: "1px solid",
  borderRight: "1px solid",
  borderColor: "divider",
  backgroundColor: "background.paper",
  ...(viewMode === "month" && day.hasSame(DateTime.now().setZone("Europe/Berlin"), "day")
    ? {
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          backgroundColor: "primary.main",
        },
      }
    : {}),
});

const hourHeaderSx = {
  px: 1.5,
  py: 1,
  fontSize: 12,
  textAlign: "left",
  borderBottom: "1px solid",
  borderRight: "1px solid",
  borderColor: "divider",
  backgroundColor: "background.paper",
};

const schedulerViewToggleGroupSx = {
  height: 32,
  borderRadius: "16px",
  backgroundColor: "action.hover",
  "& .MuiToggleButtonGroup-grouped": {
    border: 0,
    borderRadius: "16px !important",
    mx: 0,
  },
};

const schedulerViewToggleButtonSx = {
  width: 90,
  height: 32,
  color: "text.secondary",
  fontSize: 14,
  fontWeight: 500,
  textTransform: "none",
  borderColor: "transparent !important",
  "&:hover": {
    backgroundColor: "transparent",
    color: "text.secondary",
  },
  "&.Mui-selected": {
    backgroundColor: "primary.main",
    color: "primary.contrastText",
  },
  "&.Mui-selected:hover": {
    backgroundColor: "primary.main",
    color: "primary.contrastText",
  },
};

const selectAllResourcesLinkSx = {
  mt: 0.75,
  color: "text.secondary",
  fontSize: 12,
  textAlign: "left",
};

const resourceAvatarSx = {
  width: 28,
  height: 28,
  flexShrink: 0,
};

const resourceIconSx = {
  width: 28,
  height: 28,
  flexShrink: 0,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "action.hover",
};

const resourceDragHandleButtonSx = {
  width: 20,
  height: 28,
  p: 0,
  flexShrink: 0,
  color: "text.disabled",
  "&:hover": {
    backgroundColor: "transparent",
  },
};

const resourceCellSx = {
  position: "sticky",
  left: 0,
  zIndex: 4,
  minHeight: RESOURCE_ROW_HEIGHT_PX,
  pl: 0.75,
  pr: 1.5,
  py: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  backgroundColor: "background.paper",
  borderRight: "1px solid",
  borderBottom: "1px solid",
  borderColor: "divider",
};

const addResourceCellSx = {
  ...resourceCellSx,
  justifyContent: "flex-start",
  gap: 0.75,
  "& .remove-all-resources-button": {
    ml: "auto",
  },
};

const slotCellSx = (isClickable: boolean, rowHeight = RESOURCE_ROW_HEIGHT_PX) => ({
  minHeight: rowHeight,
  p: 0,
  border: 0,
  borderRight: "1px solid",
  borderBottom: "1px solid",
  borderColor: "divider",
  appearance: "none" as const,
  backgroundColor: "background.paper",
  cursor: isClickable ? "pointer" : undefined,
  "&:hover": isClickable
    ? {
        backgroundColor: "action.hover",
      }
    : undefined,
});
